// IssueFormModal — the create + edit form for an issue's metadata. Create
// collects title / description / severity / party (all required) plus an
// optional owner and contract reference, and submits a CreateIssueInput. Edit
// pre-fills from the existing issue and submits an UpdateIssueInput carrying
// the version for optimistic-concurrency. Status and the lifecycle timestamps
// are NOT edited here — they mutate only through the dedicated command
// endpoints (transition / close).
//
// Party is a required select with no silent default: an unlabelled or
// auto-defaulted party is the kind of stored-intent ambiguity we avoid.
// `IssueLinkPicker` adds optional cross-register links (deliverables /
// milestones / KPIs / KPI measurements) into the same submit payload —
// composite-unique by (targetType, targetId) at the backend schema.

import { useRef, useState } from "react";
import Modal from "../../../common/Modal";
import IssueLinkPicker from "./IssueLinkPicker";
import MarkdownToolbar from "./MarkdownToolbar";
import { handleMarkdownEnter } from "./markdownListContinuation";
import type {
  CreateIssueInput,
  DeliverableOwner,
  Issue,
  IssueLink,
  IssueSeverity,
  UpdateIssueInput,
} from "../../../../types";

interface OptionDef {
  value: string;
  label: string;
}

const SEVERITY_OPTIONS: { value: IssueSeverity; label: string }[] = [
  { value: "critical", label: "Critical" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];

const PARTY_OPTIONS: { value: DeliverableOwner; label: string }[] = [
  { value: "supplier", label: "Supplier" },
  { value: "customer", label: "Customer" },
  { value: "joint", label: "Joint" },
];

/** Optional seed values for create mode. Used by the raise-from-signal
 *  deep-link so the form lands pre-populated with title / severity / party
 *  / a single auto-link back to the source register item (e.g. a rejected
 *  deliverable or a red KPI period). Each field is optional; absent fields
 *  fall back to their existing defaults. */
export interface IssueFormInitialValues {
  title?: string;
  description?: string;
  severity?: IssueSeverity;
  party?: DeliverableOwner;
  links?: IssueLink[];
}

type IssueFormModalProps =
  | {
      mode: "create";
      issue?: undefined;
      contractId: string;
      ownerOptions: OptionDef[];
      initialValues?: IssueFormInitialValues;
      onSubmit: (input: CreateIssueInput) => void;
      onClose: () => void;
      isSubmitting: boolean;
    }
  | {
      mode: "edit";
      issue: Issue;
      contractId: string;
      ownerOptions: OptionDef[];
      onSubmit: (input: UpdateIssueInput) => void;
      onClose: () => void;
      isSubmitting: boolean;
    };

export default function IssueFormModal(props: IssueFormModalProps) {
  const { mode, contractId, ownerOptions, onClose, isSubmitting } = props;
  const existing = props.mode === "edit" ? props.issue : undefined;
  const initial = props.mode === "create" ? props.initialValues : undefined;

  const [title, setTitle] = useState(existing?.title ?? initial?.title ?? "");
  const [description, setDescription] = useState(
    existing?.description ?? initial?.description ?? "",
  );
  const [severity, setSeverity] = useState<IssueSeverity>(
    existing?.severity ?? initial?.severity ?? "medium",
  );
  const [party, setParty] = useState<DeliverableOwner>(
    existing?.party ?? initial?.party ?? "supplier",
  );
  const [ownerId, setOwnerId] = useState(existing?.ownerId ?? "");
  const [contractReference, setContractReference] = useState(
    existing?.contractReference ?? "",
  );
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const [links, setLinks] = useState<IssueLink[]>(
    existing?.links ?? initial?.links ?? [],
  );

  const titleTrimmed = title.trim();
  const descriptionTrimmed = description.trim();
  const canSubmit = !!titleTrimmed && !!descriptionTrimmed && !isSubmitting;

  const handleSubmit = () => {
    if (!titleTrimmed || !descriptionTrimmed) return;
    const base: CreateIssueInput = {
      title: titleTrimmed,
      description: descriptionTrimmed,
      severity,
      party,
      ...(ownerId ? { ownerId } : {}),
      ...(contractReference.trim()
        ? { contractReference: contractReference.trim() }
        : {}),
      // Send `links` only when at least one is selected, so the create
      // payload stays minimal for the common case. Edit always sends the
      // current state (including empty) so a user can clear all links.
      ...(props.mode === "edit" || links.length > 0 ? { links } : {}),
    };
    if (props.mode === "edit") {
      props.onSubmit({ ...base, version: props.issue.version });
    } else {
      props.onSubmit(base);
    }
  };

  const heading = mode === "create" ? "New issue" : "Edit issue";
  const submitLabel = mode === "create" ? "Create issue" : "Save changes";

  return (
    <Modal isOpen onClose={onClose} title={heading}>
      <div className="space-y-3">
        <div>
          <label htmlFor="issue-form-title" className="label">
            Title
          </label>
          <input
            id="issue-form-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="input w-full"
          />
        </div>

        <div>
          <label htmlFor="issue-form-description" className="label">
            Description
          </label>
          {/* Stored as markdown; the toolbar inserts the three constructs the
              mini renderer understands (bold / bulleted / numbered list). The
              opened thread entry + the exported PDF render it back. */}
          <MarkdownToolbar
            textareaRef={descriptionRef}
            value={description}
            onChange={setDescription}
          />
          {/* `.input` pins h-10 (single line); min-h + h-auto override it so
              the 8 rows actually render tall. resize-y lets the user grow it. */}
          <textarea
            id="issue-form-description"
            ref={descriptionRef}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onKeyDown={(e) => handleMarkdownEnter(e, description, setDescription)}
            rows={8}
            className="input h-auto min-h-[12rem] w-full resize-y rounded-t-none"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="issue-form-severity" className="label">
              Severity
            </label>
            <select
              id="issue-form-severity"
              value={severity}
              onChange={(e) => setSeverity(e.target.value as IssueSeverity)}
              className="input w-full"
            >
              {SEVERITY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="issue-form-party" className="label">
              Party
            </label>
            <select
              id="issue-form-party"
              value={party}
              onChange={(e) => setParty(e.target.value as DeliverableOwner)}
              className="input w-full"
            >
              {PARTY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="issue-form-owner" className="label">
            Owner (optional)
          </label>
          <select
            id="issue-form-owner"
            value={ownerId}
            onChange={(e) => setOwnerId(e.target.value)}
            className="input w-full"
          >
            <option value="">Unassigned</option>
            {ownerOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="issue-form-contract-ref" className="label">
            Contract reference (optional)
          </label>
          <input
            id="issue-form-contract-ref"
            value={contractReference}
            onChange={(e) => setContractReference(e.target.value)}
            placeholder="e.g. Clause 5.3"
            className="input w-full"
          />
        </div>

        <IssueLinkPicker
          contractId={contractId}
          value={links}
          onChange={setLinks}
        />

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="btn-primary"
          >
            {submitLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
}
